import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, MailCheck } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Valida força mínima da senha localmente (8+ chars, maiúscula, minúscula e número).
 * Retorna mensagem em português ou null se ok.
 */
function validatePasswordStrength(pw: string): string | null {
  if (pw.length < 8) return "A senha precisa ter pelo menos 8 caracteres.";
  if (!/[a-z]/.test(pw)) return "A senha precisa conter pelo menos uma letra minúscula.";
  if (!/[A-Z]/.test(pw)) return "A senha precisa conter pelo menos uma letra maiúscula.";
  if (!/[0-9]/.test(pw)) return "A senha precisa conter pelo menos um número.";
  return null;
}

/** Traduz mensagens de erro de senha vindas da API para português. */
function translatePasswordError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("password") && (m.includes("weak") || m.includes("strength") || m.includes("compromised") || m.includes("pwned") || m.includes("breach"))) {
    return "Senha muito fraca ou exposta em vazamentos conhecidos. Use pelo menos 8 caracteres com letras maiúsculas, minúsculas e números — evite senhas óbvias como 123456 ou sequências do seu e-mail.";
  }
  if (m.includes("password") && m.includes("should be at least")) {
    return "A senha precisa ter pelo menos 8 caracteres com letras maiúsculas, minúsculas e números.";
  }
  if (m.includes("password")) {
    return "Senha inválida. Use pelo menos 8 caracteres combinando letras maiúsculas, minúsculas e números.";
  }
  return msg;
}

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: "login" | "signup";
  /**
   * Plano que o usuário pretende assinar. Quando informado (pro/premium),
   * o modal abre direto na aba de cadastro e, ao concluir, redireciona o
   * usuário para /planos?tier=...&autostart=1 para iniciar o checkout.
   */
  intendedTier?: "free" | "pro" | "premium" | null;
}

const AuthModal = ({ open, onClose, defaultTab = "login", intendedTier = null }: AuthModalProps) => {
  const [tab, setTab] = useState(defaultTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isPaidIntent = intendedTier === "pro" || intendedTier === "premium";
  const tierLabel = intendedTier === "premium" ? "Premium" : intendedTier === "pro" ? "Pro" : null;

  // Sync tab with defaultTab only when modal opens
  const [prevOpen, setPrevOpen] = useState(false);
  if (open && !prevOpen) {
    setTab(isPaidIntent ? "signup" : defaultTab);
    setPrevOpen(true);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      setShowForgot(false);
      setSignupSentTo(null);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      toast({ variant: "destructive", title: "Erro ao entrar", description: error });
    } else {
      toast({ title: "Bem-vindo de volta!" });
      onClose();
      if (isPaidIntent) {
        navigate(`/planos?tier=${intendedTier}&autostart=1`);
      } else {
        navigate("/app");
      }
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== passwordConfirm) {
      toast({ variant: "destructive", title: "Senhas diferentes", description: "A confirmação não confere com a senha." });
      return;
    }
    const strengthError = validatePasswordStrength(password);
    if (strengthError) {
      toast({ variant: "destructive", title: "Senha não atende aos requisitos", description: strengthError });
      return;
    }
    setLoading(true);
    const { error } = await signUp(email, password, name);
    setLoading(false);
    if (error) {
      toast({ variant: "destructive", title: "Não foi possível criar a conta", description: translatePasswordError(error) });
    } else {
      // Mostra tela persistente de confirmação — toast é fácil de não notar.
      setSignupSentTo(email);
    }
  };

  const handleResendConfirmation = async () => {
    if (!signupSentTo) return;
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email: signupSentTo });
    setResending(false);
    if (error) {
      toast({ variant: "destructive", title: "Não foi possível reenviar", description: error.message });
    } else {
      toast({ title: "E-mail reenviado", description: `Enviamos novamente o link de confirmação para ${signupSentTo}.` });
    }
  };

  const handleGoogle = async () => {
    if (isPaidIntent) {
      try {
        sessionStorage.setItem("lt:pendingTier", intendedTier!);
      } catch {
        // ignore
      }
    }
    await signInWithGoogle();
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await resetPassword(forgotEmail);
    setLoading(false);
    if (error) {
      toast({ variant: "destructive", title: "Erro", description: error });
    } else {
      toast({ title: "E-mail enviado", description: "Verifique sua caixa de entrada para redefinir a senha." });
      setShowForgot(false);
    }
  };

  const inputClass = "w-full rounded-md border border-lt-border bg-lt-bg3 px-3 py-2.5 text-sm text-foreground placeholder:text-lt-cream3 focus:border-primary focus:outline-none";
  const passwordWrapClass = "relative";
  const passwordInputClass = inputClass + " pr-10";
  const eyeBtnClass = "absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-lt-cream3 hover:text-foreground";

  if (showForgot) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="border-lt-border bg-lt-bg2 sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-xl text-foreground">
              Redefinir senha
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgot} className="space-y-4">
            <p className="text-center text-xs text-lt-cream3">
              Informe seu e-mail e enviaremos um link para redefinir sua senha.
            </p>
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">E-mail</label>
              <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="seu@email.com" className={inputClass} />
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-md bg-primary py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {loading ? "Enviando..." : "Enviar link"}
            </button>
            <p className="text-center text-xs text-lt-cream3">
              <button type="button" onClick={() => setShowForgot(false)} className="text-primary hover:underline">
                Voltar ao login
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  if (signupSentTo) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="border-lt-border bg-lt-bg2 sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-xl text-foreground">
              Confirme seu e-mail
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-1">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
              <MailCheck className="h-7 w-7 text-primary" />
            </div>
            <div className="space-y-3 text-center">
              <p className="text-sm leading-relaxed text-foreground">
                Conta criada! Enviamos um link de confirmação para
                <br />
                <span className="font-medium text-primary">{signupSentTo}</span>
              </p>
              <p className="text-xs leading-relaxed text-lt-cream3">
                Abra seu e-mail e clique no link para <strong className="text-lt-cream2">validar seu cadastro</strong>.
                Sem essa confirmação você não conseguirá entrar na sua conta.
              </p>
              {isPaidIntent && (
                <p className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-lt-cream2">
                  Depois de confirmar, abra <span className="font-medium text-primary">/planos</span> para
                  finalizar a assinatura {tierLabel}.
                </p>
              )}
            </div>
            <div className="rounded-md border border-lt-border bg-lt-bg3 px-3 py-2.5 text-[11px] leading-relaxed text-lt-cream3">
              Não recebeu? Verifique a caixa de <strong>spam</strong> ou <strong>promoções</strong>. O remetente é
              <span className="text-lt-cream2"> notify@lototerminal.com.br</span>.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resending}
                className="flex-1 rounded-md border border-lt-border bg-lt-bg3 py-2.5 text-xs text-lt-cream2 hover:border-primary hover:text-primary disabled:opacity-50"
              >
                {resending ? "Reenviando…" : "Reenviar e-mail"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setSignupSentTo(null);
                  onClose();
                  if (isPaidIntent) {
                    navigate(`/planos?tier=${intendedTier}&autostart=1`);
                  }
                }}
                className="flex-1 rounded-md bg-primary py-2.5 text-xs font-medium text-primary-foreground hover:opacity-90"
              >
                Entendi
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="border-lt-border bg-lt-bg2 sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-center font-serif text-xl text-foreground">
            Loto<em className="text-primary italic">Terminal</em>
          </DialogTitle>
        </DialogHeader>

        {isPaidIntent && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-center text-xs text-lt-cream2">
            Crie sua conta para assinar o plano{" "}
            <span className="font-medium text-primary">{tierLabel}</span>. Você cai direto no
            checkout em seguida.
          </div>
        )}

        <div className="flex gap-1 rounded-lg bg-lt-bg3 p-1">
          <button onClick={() => setTab("login")} className={`flex-1 rounded-md py-2 text-xs font-medium transition-colors ${tab === "login" ? "bg-lt-bg2 text-foreground" : "text-lt-cream3 hover:text-foreground"}`}>
            Entrar
          </button>
          <button onClick={() => setTab("signup")} className={`flex-1 rounded-md py-2 text-xs font-medium transition-colors ${tab === "signup" ? "bg-lt-bg2 text-foreground" : "text-lt-cream3 hover:text-foreground"}`}>
            Criar conta
          </button>
        </div>

        {tab === "login" ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">E-mail</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">Senha</label>
              <div className={passwordWrapClass}>
                <input type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={passwordInputClass} />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className={eyeBtnClass} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-md bg-primary py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {loading ? "Entrando..." : isPaidIntent ? `Entrar e assinar ${tierLabel}` : "Entrar"}
            </button>
            <p className="text-center">
              <button type="button" onClick={() => setShowForgot(true)} className="text-xs text-lt-cream3 hover:text-primary">
                Esqueceu a senha?
              </button>
            </p>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-lt-border" />
              <span className="text-[11px] text-lt-cream3">ou</span>
              <div className="h-px flex-1 bg-lt-border" />
            </div>
            <button type="button" onClick={handleGoogle} className="w-full rounded-md border border-lt-border bg-lt-bg3 py-2.5 text-xs text-lt-cream2 hover:border-lt-cream3">
              Entrar com Google
            </button>
            <p className="text-center text-xs text-lt-cream3">
              Não tem conta?{" "}
              <button type="button" onClick={() => setTab("signup")} className="text-primary hover:underline">
                Criar agora
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSignup} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">Nome</label>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">E-mail</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" className={inputClass} />
            </div>
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">Senha</label>
              <div className={passwordWrapClass}>
                <input type={showPassword ? "text" : "password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" className={passwordInputClass} />
                <button type="button" onClick={() => setShowPassword((v) => !v)} className={eyeBtnClass} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <ul className="mt-2 space-y-0.5 text-[11px] text-lt-cream3">
                <li className={password.length >= 8 ? "text-primary" : ""}>• mínimo de 8 caracteres</li>
                <li className={/[A-Z]/.test(password) ? "text-primary" : ""}>• uma letra maiúscula (A–Z)</li>
                <li className={/[a-z]/.test(password) ? "text-primary" : ""}>• uma letra minúscula (a–z)</li>
                <li className={/[0-9]/.test(password) ? "text-primary" : ""}>• um número (0–9)</li>
                <li className="text-lt-cream3/80"><ShieldCheck className="mr-1 inline h-3 w-3" />evite senhas já vazadas (ex: 123456, qwerty)</li>
              </ul>
            </div>
            <div>
              <label className="mb-1 block text-xs text-lt-cream3">Confirmar senha</label>
              <div className={passwordWrapClass}>
                <input type={showPasswordConfirm ? "text" : "password"} required minLength={8} value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} placeholder="Repita a senha" className={passwordInputClass} />
                <button type="button" onClick={() => setShowPasswordConfirm((v) => !v)} className={eyeBtnClass} aria-label={showPasswordConfirm ? "Ocultar senha" : "Mostrar senha"}>
                  {showPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordConfirm && password !== passwordConfirm && (
                <p className="mt-1 text-[11px] text-destructive">As senhas não conferem.</p>
              )}
            </div>
            <button type="submit" disabled={loading} className="w-full rounded-md bg-primary py-3 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
              {loading
                ? "Criando..."
                : isPaidIntent
                ? `Criar conta e assinar ${tierLabel}`
                : "Criar conta grátis"}
            </button>
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-lt-border" />
              <span className="text-[11px] text-lt-cream3">ou</span>
              <div className="h-px flex-1 bg-lt-border" />
            </div>
            <button type="button" onClick={handleGoogle} className="w-full rounded-md border border-lt-border bg-lt-bg3 py-2.5 text-xs text-lt-cream2 hover:border-lt-cream3">
              Criar com Google
            </button>
            <p className="text-center text-xs text-lt-cream3">
              Já tem conta?{" "}
              <button type="button" onClick={() => setTab("login")} className="text-primary hover:underline">
                Entrar
              </button>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
